import type {
  FieldErrors,
  FieldValues,
  Resolver,
  ResolverOptions,
} from 'react-hook-form';

/**
 * Server-side (`/validate`, `/score/trip-leg`) messages keyed by form path.
 * Paths may be in bracket (`trips[0].pickupDateTime`) or dot
 * (`trips.0.pickupDateTime`) notation — both are what
 * `useTripLegValidationFormApplier` hands to `form.setError`.
 */
export type ServerFieldErrorMap = Record<string, string>;

/** Same `type` the applier uses with `form.setError`, so both paths produce identical error objects. */
export const SERVER_FIELD_ERROR_TYPE = 'manual';

function pathSegments(path: string): string[] {
  return path
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter(Boolean);
}

/** Mirrors react-hook-form's internal `set`: numeric segments create arrays. */
function setNestedError(
  target: Record<string, unknown>,
  path: string,
  value: unknown,
): void {
  const segments = pathSegments(path);
  let node: Record<string, unknown> = target;
  for (let i = 0; i < segments.length - 1; i++) {
    const key = segments[i];
    const existing = node[key];
    if (existing == null || typeof existing !== 'object') {
      node[key] = /^\d+$/.test(segments[i + 1]) ? [] : {};
    }
    node = node[key] as Record<string, unknown>;
  }
  node[segments[segments.length - 1]] = value;
}

/**
 * Wraps a react-hook-form resolver so server errors survive the resolver.
 *
 * With a resolver configured, react-hook-form treats the resolver's output as
 * the single source of truth for a field's error: every blur (in `onBlur`
 * mode), every `trigger(name)` and every `setValue(..., { shouldValidate })`
 * replaces that field's error with whatever the resolver returned — and a
 * `form.setError` from the `/validate` applier is silently dropped in the
 * process (the user saw a server error vanish the moment the field blurred).
 *
 * This wrapper re-adds the server errors that are currently active (per
 * `getServerFieldErrors`) to the resolver output of PARTIAL passes so those
 * passes reproduce the server error instead of clearing it. A server error
 * wins over a schema error on the same path, matching `setError`'s override.
 *
 * Full-form passes (`handleSubmit`, `trigger()` with no names, the background
 * `isValid` computation) are left untouched on purpose: they never read
 * server errors today and merging there would block submit / flip `isValid`
 * while the machine selectors — not the form — own that gating. Which pass is
 * "full" is decided by `isFullFormPass`, since only the form control knows
 * the mounted-field set.
 */
export function withServerFieldErrors<
  TFieldValues extends FieldValues,
  TContext,
  TTransformedValues,
>(
  base: Resolver<TFieldValues, TContext, TTransformedValues>,
  getServerFieldErrors: () => ServerFieldErrorMap,
  isFullFormPass: (options: ResolverOptions<TFieldValues>) => boolean,
): Resolver<TFieldValues, TContext, TTransformedValues> {
  return async (values, context, options) => {
    const result = await base(values, context, options);
    if (isFullFormPass(options)) {
      return result;
    }
    const serverFieldErrors = getServerFieldErrors();
    const paths = Object.keys(serverFieldErrors);
    if (paths.length === 0) {
      return result;
    }
    // The base resolver builds a fresh errors object per call, so mutating a
    // shallow copy is safe; nested nodes we touch are recreated by setNestedError
    // only when absent.
    const errors = {
      ...(result.errors as Record<string, unknown>),
    } as Record<string, unknown>;
    for (const path of paths) {
      setNestedError(errors, path, {
        type: SERVER_FIELD_ERROR_TYPE,
        message: serverFieldErrors[path],
      });
    }
    return {
      values: {},
      errors: errors as FieldErrors<TFieldValues>,
    };
  };
}
